import { useAppConfig } from "../AppConfigProvider";
import { NavigationContainer, useNavigationContainerRef } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import withAuth from "../hocs/withAuth";
import withCompanionProviders from "../hocs/withCompanionProviders";
import noAuth from "../hocs/noAuth";
import { useMemo, useRef } from "react";
import { routingInstrumentation } from "../utils/sentry";
import { useAuth } from "../hooks/useAuth";
import { UserRoleUI } from "../contexts/AuthContext";
import { StyleSheet, View } from "react-native";
import Loader from "../components/common/status/Loader/Loader";
import { getDefaultAuthenticatedRoute } from "../utils/authRoutes.utils";
import { RootStackParamList, RouteName } from "./navigation.types";

export type { RootStackParamList, RouteName } from "./navigation.types";

const Stack = createStackNavigator<RootStackParamList>();

export type RouteConfig<RouteName> = {
  name: RouteName;
  path: string;
  getComponent: () => React.ComponentType<unknown>;
  initialParams?: unknown;
};

const routeConfigStudent = (isCompanionMode: boolean, isLMS: boolean): RouteConfig<RouteName>[] =>
  [
    {
      name: "Home",
      path: "home",
      getComponent: () => withAuth(require("../views/HomeView").default),
    },
    {
      name: "Companion Home",
      path: "companion/home",
      getComponent: () => withAuth(withCompanionProviders(require("../views/CompanionHomeView").default)),
    },
    {
      name: "Companion Chat",
      path: "companion/chat",
      getComponent: () => withAuth(withCompanionProviders(require("../views/CompanionView").default)),
    },
    {
      name: "Portfolio",
      path: "portfolio",
      getComponent: () => withAuth(require("../views/PortfolioView").default),
    },
    {
      name: "Journal",
      path: "journal",
      getComponent: () => withAuth(require("../views/JournalView").default),
    },
    {
      name: "Coursework",
      path: "coursework",
      getComponent: () => withAuth(require("../views/SchoolsView").default),
    },
    {
      name: "Activities",
      path: "activities",
      getComponent: () => withAuth(require("../views/SchoolsView").default),
    },
    {
      name: "Course",
      path: "school/course",
      getComponent: () => withAuth(require("../views/CourseView").default),
    },
    {
      name: "Custom School Student Courses",
      path: "student/school",
      getComponent: () => withAuth(require("../views/CustomSchoolStudentCoursesView").default),
    },
    {
      name: "Custom Course Student",
      path: "student/school/course",
      getComponent: () => withAuth(require("../views/CustomCourseStudentView").default),
    },
    {
      name: "Custom Resource Student",
      path: "student/school/course/resource",
      getComponent: () => withAuth(require("../views/CustomResourceStudentView").default),
    },
    {
      name: "Custom Assignment Student",
      path: "student/school/course/assignment",
      getComponent: () => withAuth(require("../views/CustomAssignmentStudentView").default),
    },
    {
      name: "Custom Course AITutor",
      path: "student/school/course/aitutor",
      getComponent: () => withAuth(require("../views/CustomCourseAITutorChatView").default),
    },
    {
      name: "Custom Course Assessment",
      path: "student/school/course/assessment",
      getComponent: () => withAuth(require("../views/CustomCourseAssessmentStudentView").default),
    },
    {
      name: "Learn",
      path: "learn",
      getComponent: () => withAuth(require("../views/LearnView").default),
    },
    {
      name: "Tracks",
      path: "tracks",
      getComponent: () => withAuth(require("../views/TracksView").default),
    },
    {
      name: "Track Overview",
      path: "track/overview",
      getComponent: () => withAuth(require("../views/TrackOverviewView").default),
    },
    {
      name: "Categories",
      path: "categories",
      getComponent: () => withAuth(require("../views/CategoriesView").default),
    },
    {
      name: "Lesson",
      path: "learn/lesson",
      getComponent: () => withAuth(require("../views/LessonSubchapterView").default),
    },
    {
      name: "Lesson Activities",
      path: "learn/lesson/activities",
      getComponent: () => withAuth(require("../views/LessonLearningView").default),
    },
    {
      name: "Lesson Assessment",
      path: "learn/lesson/assessment",
      getComponent: () => withAuth(require("../views/LessonAssessmentView").default),
    },
    {
      name: "Lesson Chat",
      path: "learn/lesson/chat",
      getComponent: () => withAuth(require("../views/LessonChatView").default),
    },
    {
      name: "Lesson Video",
      path: "learn/lesson/video",
      getComponent: () => withAuth(require("../views/LessonVideoView").default),
    },
    {
      name: "Onboarding",
      path: "onboarding",
      getComponent: () => withAuth(require("../views/OnboardingView").default),
    },
    {
      name: "Settings",
      path: "settings",
      getComponent: () => withAuth(require("../views/SettingsView").default),
    },
    {
      name: "Invite",
      path: "invite",
      getComponent: () => withAuth(require("../views/InviteView").default),
    },
    {
      name: "Career",
      path: "career",
      getComponent: () => withAuth(require("../views/CareerView").default),
    },
    {
      name: "Degree",
      path: "degree",
      getComponent: () => withAuth(require("../views/DegreeView").default),
    },
    {
      name: "Project",
      path: "project",
      getComponent: () => withAuth(require("../views/ProjectView").default),
    },
    {
      name: "CareerDetails",
      path: "career/details",
      getComponent: () => withAuth(require("../views/CareerDetailView").default),
    },
    {
      name: "ThankYou",
      path: "thank-you",
      getComponent: () => withAuth(require("../views/ThankYouView").default),
    },
    {
      name: "Custom Module Student",
      path: "student/school/course/module",
      getComponent: () => withAuth(require("../views/CustomModuleStudentView").default),
    },
    {
      name: "Resource",
      path: "school/course/resource",
      getComponent: () => withAuth(require("../views/ResourceView").default),
    },
    {
      name: "School",
      path: "school",
      getComponent: () => withAuth(require("../views/SchoolView").default),
    },
    {
      name: "Announcement",
      path: "announcement",
      getComponent: () => withAuth(require("../views/AnnouncementView").default),
    },
    {
      name: "Announcement Detail",
      path: "announcement/detail",
      getComponent: () => withAuth(require("../views/AnnouncementDetailView").default),
    },
    {
      name: "Enrollment",
      path: "student/school/course/enrollment",
      getComponent: () => withAuth(require("../views/EnrollmentView").default),
    },
    {
      name: "Chat Settings",
      path: "companion/settings",
      getComponent: () => withAuth(withCompanionProviders(require("../views/ChatSettingsView").default)),
    },
    {
      name: "To do",
      path: "companion/todo",
      getComponent: () => withAuth(withCompanionProviders(require("../views/TodoView").default)),
    },
    {
      name: "Learning Activities",
      path: "companion/learning-activities",
      getComponent: () => withAuth(withCompanionProviders(require("../views/CompanionActivityView").default)),
    },
    {
      name: "Interactive Learning Environment",
      path: "companion/learning-activity",
      getComponent: () => withAuth(require("../views/LearningActivityView").default),
    },
    {
      name: "Insights",
      path: "student/insights",
      getComponent: () => withAuth(require("../views/InsightsView").default),
    },
  ] as const satisfies RouteConfig<RouteName>[];

const routeConfigFaculty = (isCompanionMode: boolean, isLMS: boolean): RouteConfig<RouteName>[] =>
  [
    {
      name: "Student Analytics",
      path: "student-analytics",
      getComponent: () =>
        withAuth(require("../views/StudentAnalyticsView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Custom School Faculty Courses",
      path: "school/courses",
      getComponent: () =>
        withAuth(require("../views/CustomSchoolFacultyCoursesView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
          redirectToIfNoPermission: !isCompanionMode ? "Custom School Student Courses" : "Companion Home",
        }),
    },
    {
      name: "Custom Coursework Modules",
      path: "school/course/modules",
      getComponent: () =>
        withAuth(require("../views/CustomCourseworkModulesView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
          redirectToIfNoPermission: !isCompanionMode ? "Custom Course Student" : "Companion Home",
        }),
    },
    {
      name: "Course Settings",
      path: "school/course/settings",
      getComponent: () =>
        withAuth(require("../views/CourseSettingsView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
          redirectToIfNoPermission: !isCompanionMode ? "Custom School Student Courses" : "Companion Home",
        }),
    },
    {
      name: "Faculty",
      path: "faculty",
      getComponent: () =>
        withAuth(require("../views/FacultyView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Course Modernizer",
      path: "admin/course-modernizer",
      getComponent: () =>
        withAuth(require("../views/adminViews/CourseEvaluatorView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Course Modernizer Details",
      path: "admin/course-modernizer/details",
      getComponent: () =>
        withAuth(require("../views/adminViews/CourseEvaluatorDetailView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Nudge Management",
      path: "admin/nudge-management",
      getComponent: () =>
        withAuth(require("../views/adminViews/NudgeManagementView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
  ] as const satisfies RouteConfig<RouteName>[];

const routeConfigAdmin = (isCompanionMode: boolean, isLMS: boolean): RouteConfig<RouteName>[] =>
  [
    {
      name: "Admin",
      path: "dashboard",
      getComponent: () =>
        withAuth(require("../views/AdminView").default, {
          minRequiredRole: UserRoleUI.ADMIN,
        }),
    },
    {
      name: "Create Tenant",
      path: "dashboard/tenant/create",
      getComponent: () =>
        withAuth(require("../views/CreateTenantView").default, {
          minRequiredRole: UserRoleUI.ADMIN,
        }),
    },
    {
      name: "Tenant Dashboard",
      path: "dashboard/tenant",
      getComponent: () =>
        withAuth(require("../views/TenantDashboardView").default, {
          minRequiredRole: UserRoleUI.ADMIN,
        }),
    },
    {
      name: "Tenant Settings",
      path: "dashboard/tenant/settings",
      getComponent: () =>
        withAuth(require("../views/TenantSettingsDashboardView").default, {
          minRequiredRole: UserRoleUI.ADMIN,
        }),
    },
    {
      name: "OAuth Client Dashboard",
      path: "dashboard/oauth-client",
      getComponent: () =>
        withAuth(require("../views/OAuthClientDashboardView").default, {
          minRequiredRole: UserRoleUI.ADMIN,
        }),
    },
  ] as const satisfies RouteConfig<RouteName>[];

const routeConfigSchoolAdmin = (
  isCompanionMode: boolean,
  isLMS: boolean,
  userRole: UserRoleUI
): RouteConfig<RouteName>[] =>
  [
    isCompanionMode
      ? userRole === UserRoleUI.SCHOOL_ADMIN || userRole === UserRoleUI.ADMIN || userRole === UserRoleUI.AUDITOR
        ? {
            name: "Admin Companion Dashboard",
            path: "admin/dashboard",
            getComponent: () =>
              withAuth(require("../views/adminViews/AICompanionDashboardView").default, {
                minRequiredRole: UserRoleUI.AUDITOR,
              }),
          }
        : {
            name: "Faculty Companion Dashboard",
            path: "admin/dashboard",
            getComponent: () =>
              withAuth(require("../views/adminViews/FacultyDashboardCompanionView").default, {
                minRequiredRole: UserRoleUI.FACULTY,
              }),
          }
      : {
          name: "Admin Dashboard",
          path: "admin/dashboard",
          getComponent: () =>
            withAuth(require("../views/adminViews/AdminDashboardView").default, {
              minRequiredRole: UserRoleUI.FACULTY,
            }),
        },
    ...(isCompanionMode
      ? ([
          {
            name: "Safety Support",
            path: "admin/safety-support",
            getComponent: () =>
              withAuth(require("../views/adminViews/SafetySupportView").default, {
                minRequiredRole: UserRoleUI.ADMIN,
              }),
          },
        ] as const satisfies RouteConfig<RouteName>[])
      : []),
    {
      name: "Manage Courses",
      path: "admin/manage-courses",
      getComponent: () =>
        withAuth(require("../views/adminViews/ManageCoursesView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Teaching",
      path: "admin/teaching",
      getComponent: () =>
        withAuth(require("../views/adminViews/FacultyTeachingView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Degrees & Programs",
      path: "admin/degrees-programs",
      getComponent: () =>
        withAuth(require("../views/adminViews/DegreesProgramsView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Degree Information",
      path: "admin/degree-information",
      getComponent: () =>
        withAuth(require("../views/adminViews/DegreeInformationView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Course Information",
      path: "admin/course-information",
      getComponent: () =>
        withAuth(require("../views/adminViews/CourseInformationView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Course Information For Particular Student",
      path: "admin/course-information/particular-student",
      getComponent: () =>
        withAuth(require("../views/adminViews/CourseInformationParticularStudentView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Students",
      path: "admin/students",
      getComponent: () =>
        withAuth(require("../views/adminViews/StudentsListView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Student Profile",
      path: "admin/students/profile",
      getComponent: () =>
        withAuth(require("../views/adminViews/StudentProfileView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Admin Users",
      path: "admin/users",
      getComponent: () =>
        withAuth(require("../views/adminViews/AdminUsersView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Admin Grading Center",
      path: "admin/grading-center",
      getComponent: () =>
        withAuth(require("../views/adminViews/GradingCenterView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Admin Analytics",
      path: "admin/analytics",
      getComponent: () =>
        withAuth(require("../views/adminViews/AdminAnalyticsView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Admin Settings",
      path: "admin/settings",
      getComponent: () =>
        withAuth(require("../views/adminViews/AdminSettingsView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Admin Profile",
      path: "admin/profile",
      getComponent: () =>
        withAuth(require("../views/adminViews/AdminProfileView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Session Analytics",
      path: "session-analytics",
      getComponent: () =>
        withAuth(require("../views/adminViews/SessionAnalyticsView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "User Session Details",
      path: "session-analytics/user",
      getComponent: () =>
        withAuth(require("../views/adminViews/UserSessionDetailsView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Admin Announcements",
      path: "admin/announcements",
      getComponent: () =>
        withAuth(require("../views/adminViews/AdminAnnouncementsView").default, {
          minRequiredRole: UserRoleUI.SCHOOL_ADMIN,
        }),
    },
  ] as const satisfies RouteConfig<RouteName>[];

const routeConfigAuth = (isCompanionMode: boolean, isLMS: boolean): RouteConfig<RouteName>[] =>
  [
    {
      name: "Login",
      path: "login",
      getComponent: () => noAuth(require("../views/LoginView").default),
      initialParams: { isLMS },
    },
    {
      name: "LoginSuperAdmin",
      path: "admin/login",
      getComponent: () => noAuth(require("../views/LoginView").default),
      initialParams: { isLMS },
    },
    {
      name: "LoginLTI",
      path: "login/lti",
      getComponent: () => noAuth(require("../views/LoginLTIView").default),
    },
    {
      name: "LoginSSO",
      path: "login/sso",
      getComponent: () => noAuth(require("../views/LoginSSOView").default),
    },
    {
      name: "Signup",
      path: "signup",
      getComponent: () => noAuth(require("../views/SignupView").default),
      initialParams: { isLMS },
    },
    {
      name: "Reset Password",
      path: "reset-password",
      getComponent: () => noAuth(require("../views/ResetPasswordView").default),
    },
    {
      name: "Set Password",
      path: "set-password",
      getComponent: () => require("../views/SetPasswordView").default,
    },
    {
      name: "Verify Email",
      path: "verify-email",
      getComponent: () => noAuth(require("../views/VerifyEmailView").default),
    },
    {
      name: "EdlinkSuccess",
      path: "edlink-success",
      getComponent: () => withAuth(require("../views/EdlinkSuccessView").default),
    },
  ] as const satisfies RouteConfig<RouteName>[];

export const routeConfig: (
  isCompanionMode: boolean,
  isLMS: boolean,
  userRole: UserRoleUI
) => RouteConfig<RouteName>[] = (isCompanionMode: boolean, isLMS: boolean, userRole: UserRoleUI) =>
  [
    ...routeConfigStudent(isCompanionMode, isLMS),
    ...routeConfigFaculty(isCompanionMode, isLMS),
    ...routeConfigAdmin(isCompanionMode, isLMS),
    ...routeConfigSchoolAdmin(isCompanionMode, isLMS, userRole),
    ...routeConfigAuth(isCompanionMode, isLMS),
  ] as const satisfies RouteConfig<RouteName>[];

function AppNavigator() {
  const { currentDomain, isLMS, isCompanionMode } = useAppConfig();
  const navigation = useNavigationContainerRef<RootStackParamList>();
  const componentCacheRef = useRef<Record<string, React.ComponentType<unknown>>>({});

  const { userInfo, isAuthenticated, isEmailVerified, otpVerifiedForSession, isAuthReady } = useAuth();

  const isVerified = isEmailVerified && otpVerifiedForSession;
  const initialRoute = useMemo<RouteName>(() => {
    if (!isAuthenticated) {
      return "Login";
    }
    if (!isVerified) {
      return "Verify Email";
    }
    return getDefaultAuthenticatedRoute({ userRole: userInfo.userRole, isCompanionMode });
  }, [isAuthenticated, isVerified, isCompanionMode, userInfo.userRole]);

  const routes = routeConfig(isCompanionMode, isLMS, userInfo.userRole);

  const screens = {};

  for (const route of routes) {
    screens[route.name] = route.path;
  }

  const linking = {
    prefixes: [`https://${currentDomain}`, "primer://"],
    config: {
      screens: screens,
    },
  };

  if (!isAuthReady || userInfo.userRole === undefined) {
    return (
      <View style={styles.loaderContainer}>
        <Loader />
      </View>
    );
  }

  return (
    <NavigationContainer
      linking={linking}
      ref={navigation}
      onReady={() => {
        routingInstrumentation.registerNavigationContainer(navigation);
      }}>
      <Stack.Navigator key={initialRoute} initialRouteName={initialRoute} screenOptions={{ headerShown: false }}>
        {routes.map((route) => (
          <Stack.Screen
            key={route.name}
            name={route.name as keyof RootStackParamList}
            getComponent={() => {
              const cacheKey = String(route.name);
              const cached = componentCacheRef.current[cacheKey];
              if (cached) {
                return cached;
              }
              const resolved = route.getComponent();
              componentCacheRef.current[cacheKey] = resolved;
              return resolved;
            }}
            initialParams={route.initialParams}
          />
        ))}
      </Stack.Navigator>
    </NavigationContainer>
  );
}

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
});

export default AppNavigator;
